import { useTranslation } from 'react-i18next'
import { Progress } from '@structural-climbing/ui'

interface PlanStat {
  name: string
  count: number
}

interface Props {
  data: PlanStat[]
  loading?: boolean
  total?: number
}

/**
 * Subscriptions per plan as bars, largest first. Easier to compare at a glance
 * than the pie it replaced, and the plan names never have to fit inside a slice.
 */
export function SubscriptionPlanChart({ data, loading = false, total }: Props) {
  const { t } = useTranslation()
  const sorted = [...data].sort((a, b) => b.count - a.count)
  const sum = total ?? data.reduce((acc, d) => acc + d.count, 0)
  const max = sorted[0]?.count ?? 0

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-base font-semibold">{t('subscriptions.chartSubtitle')}</h2>
        {total !== undefined && total > 0 && (
          <span className="text-sm text-foreground/55">
            {t('subscriptions.totalSubscriptions', { count: total })}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-8 animate-pulse rounded-lg bg-default-100" />
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <p className="py-8 text-center text-sm text-foreground/50">
          {t('subscriptions.chartEmpty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-4">
          {sorted.map((plan) => (
            <li key={plan.name} className="flex flex-col gap-1.5">
              <div className="flex items-baseline gap-2 text-sm">
                <span className="min-w-0 flex-1 truncate">{plan.name}</span>
                <span className="text-xs text-foreground/45 tabular-nums">
                  {sum > 0 ? `${Math.round((plan.count / sum) * 100)}%` : ''}
                </span>
                <span className="w-10 text-right font-semibold tabular-nums">{plan.count}</span>
              </div>
              <Progress
                aria-label={plan.name}
                size="md"
                color="primary"
                value={max > 0 ? (plan.count / max) * 100 : 0}
                classNames={{ track: 'h-2.5 bg-default-100', indicator: 'rounded-full' }}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
